import {
  addDays,
  addMinutes,
  format,
  isSameDay,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import { da, ru } from 'date-fns/locale';
import React, { SetStateAction, useEffect, useState } from 'react';

import ArrowLeft from '../../assets/icons/ArrowLeft';
import ArrowRight from '../../assets/icons/ArrowRight';
import DefaultButton from '../default-button/DefaultButton';

import { useUniversalFind } from '../../hooks/universal';
import { Appointments, EventType } from '../../misc/types';

import { showSnackbar, userHasRole } from '../../misc/utils';
import { useGlobalMiscState } from '../../stores/misc';
import CopyWeekScheduleModal from '../copy-week-schedule-modal/CopyWeekScheduleModal';
import WeeklyCalendarEventModal from '../weekly-calendar-event-modal/WeeklyCalendarEventModal';
import WeeklyCalendarTable from '../weekly-calendar-table/WeeklyCalendarTable';
import { accessGlobalUserState } from '../../stores/user';

/** A helper to build a grid-like array from the list of events. */
function buildGrid(
  events: EventType[],
  days: Date[],
  timeSlots: string[]
): (null | 'skip' | { event: EventType; rowSpan: number })[][] {
  const rows = timeSlots.length;
  const cols = days.length;
  const grid = Array.from({ length: rows }, () => Array(cols).fill(null));

  for (let d = 0; d < cols; d++) {
    for (let t = 0; t < rows; t++) {
      if (grid[t][d] !== null) continue;

      // Find event that starts exactly at this day/time
      const day = days[d];
      const slotTime = timeSlots[t];

      const found = events.find((evt) => {
        // Compare day ignoring time
        if (!isSameDay(startOfDay(evt.day), startOfDay(day))) return false;
        return evt.start === slotTime;
      });

      if (found) {
        // Calculate how many 30-min slots this event spans
        const [sh, sm] = found.start.split(':').map(Number);
        const [eh, em] = found.end.split(':').map(Number);
        const duration = eh * 60 + em - (sh * 60 + sm); // in minutes
        const slotsCount = duration / 30;

        grid[t][d] = { event: found, rowSpan: slotsCount };

        // Mark subsequent rows as 'skip'
        for (let skip = 1; skip < slotsCount; skip++) {
          if (t + skip < rows) {
            grid[t + skip][d] = 'skip';
          }
        }
      }
    }
  }
  return grid;
}

function isOverlapping(
  start1: Date,
  end1: Date,
  start2: Date,
  end2: Date
): boolean {
  // Overlap exists if the start is before another's end AND the end is after another's start
  return start1 < end2 && end1 > start2;
}

const WeeklyCalendar = ({ localEvents, setLocalEvents, mode = 'self' }: { localEvents: EventType[], setLocalEvents: React.Dispatch<React.SetStateAction<EventType[]>>, mode?: string }) => {
  const miscState = useGlobalMiscState();
  const userState = accessGlobalUserState()
  const user = userState.get()
  const uniqUser = user.uniqUser || {}
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [popupVisibleIndex, setPopupVisibleIndex] = useState<number | null>(null);
  const [copyWeekVisible, setCopyWeekVisible] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEvent, setEditingEvent] = useState<EventType | null>(null);
  const [copyDaySelectedDate, setCopyDaySelectedDate] = useState<Date | null>(null);
  const [isNewEvent, setIsNewEvent] = useState(false);
  const [popupPosition, setPopupPosition] = useState<{
    top?: number;
    left: number;
    right: number;
    realTop?: number;
  } | null>(null);
  const psychologyCalendarTriggerFetch = miscState.get('psychologyCalendarTriggerFetch') || 0;
  const hasUserRole = userHasRole('create_psychologist_appointments')
  const isAdmin = mode === 'admin';

  /** Fetch appointments from backend */
  // const appointments = useUniversalFind(
  //   'PsychologistAppointment',
  //   {
  //     main_user_id: uniqUser.id
  //   },
  //   {
  //     include: [
  //       {
  //         model: 'PsychologistAppointmentClient',
  //         as: 'clients',
  //         include: ['client_user'],
  //       },
  //     ],
  //   }, psychologyCalendarTriggerFetch) as Appointments | null;

  // /** Convert appointments to simplified EventType[] */
  // const eventsFromServer = React.useMemo(() => {
  //   if (!appointments || !Array.isArray(appointments)) return [];
  //   return appointments.map((appointment) => {
  //     const clients = appointment.clients
  //       .filter(c => [-1, 0, 1, 2].includes(c.status))
  //       .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  //     const client = clients[0]?.client_user;
  //     const first = client?.json_saved_data?.firstname || '';
  //     const last = client?.json_saved_data?.lastname || '';
  //     const body: EventType = {
  //       id: appointment.id,
  //       title: client
  //         ? `${first} ${last}`.trim() || 'Занято'
  //         : 'Свободное время',
  //       start: format(new Date(appointment.start_time), 'HH:mm'),
  //       end: format(new Date(appointment.end_time), 'HH:mm'),
  //       day: new Date(appointment.start_time),
  //       canceled: appointment.status === -1,
  //     };
  //     if (client) {
  //       body.client = client.json_saved_data;
  //       body.clientSelf = clients[0];
  //     }
  //     return body;
  //   });
  // }, [appointments]);

  const eventsFromServer = localEvents;

  /** Generate days for the current week */
  const startOfWeekDate = startOfWeek(currentWeek, { weekStartsOn: 1 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(startOfWeekDate, i));

  /** Generate time slots from 07:30 ... 18:00 by 30 minutes */
  const timeSlots = React.useMemo(() => {
    const slots: string[] = [];
    for (let hour = 7; hour < 18; hour++) {
      if (hour === 7) {
        slots.push('07:30');
        continue;
      }
      const hh = hour.toString().padStart(2, '0');
      slots.push(`${hh}:00`);
      slots.push(`${hh}:30`);
    }
    slots.push('18:00');
    return slots;
  }, []);

  /**
   * We keep a local array of events to allow "immediate" UI changes
   * (adding new free slots, editing times) without waiting for the server.
   */
  // const [localEvents, setLocalEvents] = useState<EventType[]>([]);

  /**
   * Whenever `eventsFromServer` changes (e.g., on first load),
   * sync them into localEvents so we start from the real data.
   */
  // useEffect(() => {
  //   setLocalEvents(eventsFromServer);
  // }, [eventsFromServer]);

  /** Build a grid from localEvents for the table to render */
  const localGrid = React.useMemo(() => {
    return buildGrid(localEvents, days, timeSlots);
  }, [localEvents, days, timeSlots]);

  /** Move to previous/next week */
  const handlePrevWeek = () => setCurrentWeek((w) => addDays(w, -7));
  const handleNextWeek = () => setCurrentWeek((w) => addDays(w, 7));
  const handleToday = () =>
    setCurrentWeek(startOfWeek(new Date(), { weekStartsOn: 1 }));

  const handleAddFreeTime = (
    day: Date,
    start: string,
    end: string,
    tdElement: HTMLTableCellElement
  ) => {
    if (!hasUserRole || isAdmin) {
      return
    }
    setLocalEvents((evt) => evt.filter(e => !e.isTemporary))
    // 1) Convert the incoming day + start/end strings into actual Date objects
    const startTime = new Date(day);
    const [startH, startM] = start.split(':').map(Number);
    startTime.setHours(startH, startM, 0, 0);

    const endTime = new Date(day);
    const [endH, endM] = end.split(':').map(Number);
    endTime.setHours(endH, endM, 0, 0);

    // 2) Check if this new block conflicts with *any* existing localEvents
    const hasConflict = localEvents.some((evt) => {
      const evtStart = new Date(evt.day);
      const [evtSh, evtSm] = evt.start.split(':').map(Number);
      evtStart.setHours(evtSh, evtSm, 0, 0);

      const evtEnd = new Date(evt.day);
      const [evtEh, evtEm] = evt.end.split(':').map(Number);
      evtEnd.setHours(evtEh, evtEm, 0, 0);

      return isOverlapping(startTime, endTime, evtStart, evtEnd);
    });

    // 3) If there's a conflict, reduce the "endTime" to startTime + 30 minutes
    if (hasConflict) {
      const newEnd = addMinutes(startTime, 30);
      endTime.setTime(newEnd.getTime());
    }

    // 4) Create the event object with the final start/end times
    const newEvt: EventType = {
      createdAt: new Date(),
      id: Math.random(),
      isTemporary: true,
      title: 'Свободное время',
      start: format(startTime, 'HH:mm'),
      end: format(endTime, 'HH:mm'),
      day,
      canceled: false,
    };

    // 5) Push it into local events immediately, so user sees it
    setLocalEvents((prev) => [...prev, newEvt]);

    // 6) Open your modal for further editing
    setEditingEvent(newEvt);
    setIsNewEvent(true);
    setModalVisible(true);

    // optional: position your popup
    const rect = tdElement.getBoundingClientRect();
    setPopupPosition({
      top: rect.top - 50,
      left: rect.left - 500,
      right: rect.right - 660,
    });
  };

  // useEffect(() => {
  //   if (!modalVisible) {
  //     setLocalEvents(prev => prev.filter(e => !e.isTemporary))
  //   }
  // }, [modalVisible])

  /** Called by the table when the user clicks an EXISTING event cell => edit it */
  const handleEditEvent = (evt: EventType, tdElement: HTMLTableCellElement) => {
    if (!hasUserRole) {
      return
    }
    setEditingEvent(evt);
    setIsNewEvent(false);
    setModalVisible(true);

    const rect = tdElement.getBoundingClientRect();
    setPopupPosition({
      top: rect.top - 50,
      left: rect.left - 500,
      right: rect.right - 660,
    });
  };

  /**
   * Called from the modal whenever user types a new start/end/time => we
   * update that event in localEvents so the UI re-renders immediately.
   */
  const handleUpdateEvent = (updated: EventType) => {
    setLocalEvents((prev) =>
      prev.map((ev) => (ev.id === updated.id ? { ...updated } : ev))
    );
  };

  /**
   * Called from the modal on final "Сохранить" => do your backend call
   * to create/update the event. If success, close the modal, or optionally
   * refetch from the server.
   */
  const handleSaveSuccess = () => {
    // For example, we might refetch the server data or just trust local state:
    // refetchAppointments();
    // setModalVisible(false);
    // setIsNewEvent(false);
    // setEditingEvent(null);
    setTimeout(() => {
      setModalVisible(false);
      miscState.set('psychologyCalendarTriggerFetch', psychologyCalendarTriggerFetch + 1);
    }, 500);
  };

  const handlePopupClose = (removeNewEvent: boolean) => {
    if (removeNewEvent && editingEvent?.id) {
      setLocalEvents((prev) => {
        const temporaryEvents = prev.filter(e => e.isTemporary).sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0)).slice(1).map(e => e.id);
        return prev.filter(e => !temporaryEvents.includes(e.id))
      });
    }
    setModalVisible(false);
    setIsNewEvent(false);
    setEditingEvent(null);
  };

  const handleCopyDayClick = (tdElement: HTMLButtonElement, day: Date) => {
    if (!hasUserRole) {
      return
    }
    setCopyDaySelectedDate(day);
    setCopyWeekVisible(true);
    const rect = tdElement.getBoundingClientRect();
    setPopupPosition({
      top: rect.top - 50,
      left: rect.left - 420,
      right: rect.right - 230,
    });
  }

  const handleCopyWeekClose = () => {
    setPopupVisibleIndex(null);
    setPopupPosition(null);
    setCopyWeekVisible(false);
  };

  const handleCopyWeekClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!hasUserRole) {
      return
    }
    setCopyDaySelectedDate(null);
    setCopyWeekVisible(true);
    const rect = e.currentTarget.getBoundingClientRect();
    console.log(rect)
    setPopupPosition({
      realTop: rect.top - 50,
      left: rect.left - 420,
      right: rect.right,
    });
  }

  return (
    <div className="bg-white rounded-lg flex-1 p-4 font-['SF Pro Text'] relative overflow-y-auto">
      {/* Top controls */}
      <div className="flex justify-between items-center mb-4 overflow-auto">
        <div className="flex gap-4 items-center">
          <DefaultButton
            buttonText="Сегодня"
            additionalStyle="pt-1.5 pr-3 pb-1.5 pl-3 border border-[#AFB7CA] text-[13px] text-[#5F6B88]"
            onClick={handleToday}
          />
          <div className="flex gap-2 items-center">
            <DefaultButton
              additionalStyle="border-none text-[#5F6B88] w-[24px] h-[24px]"
              Icon={ArrowLeft}
              onClick={handlePrevWeek}
            />
            <DefaultButton
              additionalStyle="border-none text-[#5F6B88] w-[24px] h-[24px]"
              Icon={ArrowRight}
              onClick={handleNextWeek}
            />
          </div>
          <p className="text-[20px] text-[#1A1A1A] font-medium">
            {format(startOfWeekDate, 'LLLL yyyy', { locale: ru })}
          </p>
        </div>
        <DefaultButton
          additionalStyle="pt-1.5 pr-3 pb-1.5 pl-3 border border-[#AFB7CA] text-[13px] text-[#5F6B88]"
          buttonText="Скопировать расписание недели"
          onClick={handleCopyWeekClick}
        />
      </div>

      {/* Main Table */}
      <WeeklyCalendarTable
        days={days}
        timeSlots={timeSlots}
        grid={localGrid}
        onEmptyCellClick={handleAddFreeTime}
        onEventClick={handleEditEvent}
        onCopyWeekClick={(tdElement: HTMLButtonElement, day: Date) => {
          handleCopyDayClick(tdElement, day);
        }}
        popupVisibleIndex={popupVisibleIndex}
        setPopupVisibleIndex={setPopupVisibleIndex}
      />

      {copyWeekVisible && popupPosition && (
        <div
          className="absolute bg-white rounded-lg shadow-lg"
          style={{
            ...(popupPosition.realTop ? { top: popupPosition.realTop + 15 } : {}),
            ...(popupPosition.top ? { bottom: popupPosition.top + 320 } : {}),
            left: popupPosition.left - 400,
            zIndex: 1000,
          }}
        >
          <CopyWeekScheduleModal
            days={days}
            selectedDay={copyDaySelectedDate}
            onClose={handleCopyWeekClose}
            onCopySchedule={() => {
              miscState.set('psychologyCalendarTriggerFetch', psychologyCalendarTriggerFetch + 1)
              handleCopyWeekClose()
            }}
          />
        </div>
      )}

      {/* Event Editing Modal (New or Edit) */}
      {popupPosition && modalVisible && editingEvent && (
        <div
          className="absolute bg-white rounded-lg shadow-lg"
          style={{
            top: popupPosition.top,
            left: popupPosition.right,
            zIndex: 1000,
          }}
        >
          <WeeklyCalendarEventModal
            mode={mode}
            event={editingEvent}
            isNew={isNewEvent}
            onClose={handlePopupClose}
            onChange={handleUpdateEvent}
            onSaveSuccess={handleSaveSuccess}
            events={localEvents}
          />
        </div>
      )}
    </div>
  );
};

export default WeeklyCalendar;
